function cn(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ")
}

function ShellButton({
  children,
  onClick,
  variant: _variant,
}: {
  children: React.ReactNode
  onClick?: () => void
  variant?: string
}) {
  return (
    <button
      type="button"
      className="rounded-lg px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
      onClick={onClick}
    >
      {children}
    </button>
  )
}

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PropsWithChildren,
  type ReactNode,
} from "react"


type DockItem = {
  id: string
  title: string
  onRestore: () => void
  onClose?: () => void
}

type WindowDockContextValue = {
  registerDockItem: (item: DockItem) => void
  unregisterDockItem: (id: string) => void
}

type WindowRect = {
  x: number
  y: number
  width: number
  height: number
}

type ResizeDirection =
  | "top"
  | "right"
  | "bottom"
  | "left"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"

type WindowShellProps = PropsWithChildren<{
  open: boolean
  windowId: string
  title: string
  description?: string
  footer?: ReactNode
  onClose: () => void
  initialWidth?: number
  initialHeight?: number
  minWidth?: number
  minHeight?: number
  initialPosition?: {
    x: number
    y: number
  }
  allowMinimize?: boolean
  allowFullscreen?: boolean
  defaultFullscreen?: boolean
  className?: string
  headerExtras?: ReactNode
}>

const WINDOW_MARGIN = 16
const WindowDockContext = createContext<WindowDockContextValue | null>(null)

let nextWindowZIndex = 1200

function readViewportSize() {
  if (typeof window === "undefined") {
    return { width: 1440, height: 960 }
  }
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  }
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) {
    return min
  }
  return Math.min(Math.max(value, min), max)
}

function buildInitialRect(params: {
  initialWidth: number
  initialHeight: number
  minWidth: number
  minHeight: number
  initialPosition?: {
    x: number
    y: number
  }
}): WindowRect {
  const viewport = readViewportSize()
  const width = clamp(
    params.initialWidth,
    params.minWidth,
    Math.max(params.minWidth, viewport.width - WINDOW_MARGIN * 2),
  )
  const height = clamp(
    params.initialHeight,
    params.minHeight,
    Math.max(params.minHeight, viewport.height - WINDOW_MARGIN * 2),
  )
  const x = params.initialPosition
    ? clamp(
        params.initialPosition.x,
        WINDOW_MARGIN,
        viewport.width - width - WINDOW_MARGIN,
      )
    : clamp(
        viewport.width - width - 48,
        WINDOW_MARGIN,
        viewport.width - width - WINDOW_MARGIN,
      )
  const y = params.initialPosition
    ? clamp(
        params.initialPosition.y,
        WINDOW_MARGIN,
        viewport.height - height - WINDOW_MARGIN,
      )
    : clamp(
        Math.round((viewport.height - height) / 2),
        WINDOW_MARGIN,
        viewport.height - height - WINDOW_MARGIN,
      )
  return { x, y, width, height }
}

function clampRect(
  rect: WindowRect,
  minWidth: number,
  minHeight: number,
): WindowRect {
  const viewport = readViewportSize()
  const width = clamp(
    rect.width,
    minWidth,
    Math.max(minWidth, viewport.width - WINDOW_MARGIN * 2),
  )
  const height = clamp(
    rect.height,
    minHeight,
    Math.max(minHeight, viewport.height - WINDOW_MARGIN * 2),
  )
  const x = clamp(rect.x, WINDOW_MARGIN, viewport.width - width - WINDOW_MARGIN)
  const y = clamp(rect.y, WINDOW_MARGIN, viewport.height - height - WINDOW_MARGIN)
  return { x, y, width, height }
}

function applyResize(params: {
  rect: WindowRect
  direction: ResizeDirection
  deltaX: number
  deltaY: number
  minWidth: number
  minHeight: number
}): WindowRect {
  const viewport = readViewportSize()
  let nextRect = { ...params.rect }

  if (params.direction.includes("right")) {
    nextRect.width = clamp(
      params.rect.width + params.deltaX,
      params.minWidth,
      viewport.width - params.rect.x - WINDOW_MARGIN,
    )
  }
  if (params.direction.includes("left")) {
    const nextX = clamp(
      params.rect.x + params.deltaX,
      WINDOW_MARGIN,
      params.rect.x + params.rect.width - params.minWidth,
    )
    nextRect.width = clamp(
      params.rect.width - (nextX - params.rect.x),
      params.minWidth,
      viewport.width - WINDOW_MARGIN * 2,
    )
    nextRect.x = nextX
  }
  if (params.direction.includes("bottom")) {
    nextRect.height = clamp(
      params.rect.height + params.deltaY,
      params.minHeight,
      viewport.height - params.rect.y - WINDOW_MARGIN,
    )
  }
  if (params.direction.includes("top")) {
    const nextY = clamp(
      params.rect.y + params.deltaY,
      WINDOW_MARGIN,
      params.rect.y + params.rect.height - params.minHeight,
    )
    nextRect.height = clamp(
      params.rect.height - (nextY - params.rect.y),
      params.minHeight,
      viewport.height - WINDOW_MARGIN * 2,
    )
    nextRect.y = nextY
  }

  return clampRect(nextRect, params.minWidth, params.minHeight)
}

function nextZIndex(): number {
  nextWindowZIndex += 1
  return nextWindowZIndex
}

function useWindowDock() {
  return useContext(WindowDockContext)
}

export function WindowDockProvider({
  children,
}: PropsWithChildren) {
  const [dockItems, setDockItems] = useState<DockItem[]>([])

  const registerDockItem = useCallback((item: DockItem) => {
    setDockItems((current) => {
      const nextItems = current.filter((entry) => entry.id !== item.id)
      nextItems.push(item)
      return nextItems
    })
  }, [])

  const unregisterDockItem = useCallback((id: string) => {
    setDockItems((current) => current.filter((item) => item.id !== id))
  }, [])

  const contextValue = useMemo(
    () => ({
      registerDockItem,
      unregisterDockItem,
    }),
    [registerDockItem, unregisterDockItem],
  )

  return (
    <WindowDockContext.Provider value={contextValue}>
      {children}
      {dockItems.length > 0 ? (
        <div className="pointer-events-none fixed bottom-4 left-0 right-0 z-[1600] flex justify-center px-4">
          <div className="pointer-events-auto flex flex-wrap items-center justify-center gap-2 rounded-[20px] border border-slate-200 bg-white/95 px-3 py-2 shadow-lg backdrop-blur-[10px]">
            {dockItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2 py-1.5"
              >
                <ShellButton variant="ghost" onClick={item.onRestore}>
                  {item.title}
                </ShellButton>
                {item.onClose ? (
                  <ShellButton variant="ghost" onClick={item.onClose}>
                    Закрыть
                  </ShellButton>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </WindowDockContext.Provider>
  )
}

export function WindowShell({
  open,
  windowId,
  title,
  description,
  footer,
  onClose,
  initialWidth = 960,
  initialHeight = 720,
  minWidth = 520,
  minHeight = 340,
  initialPosition,
  allowMinimize = true,
  allowFullscreen = true,
  defaultFullscreen = false,
  className,
  headerExtras,
  children,
}: WindowShellProps) {
  const dock = useWindowDock()
  const [minimized, setMinimized] = useState(false)
  // 17.04.26: fullscreen-тоггл — чтобы плановый конструктор по умолчанию
  // занимал весь экран и не съедал рабочую область AppShell.
  const [fullscreen, setFullscreen] = useState(defaultFullscreen)
  const [rect, setRect] = useState(() =>
    buildInitialRect({
      initialWidth,
      initialHeight,
      minWidth,
      minHeight,
      initialPosition,
    }),
  )
  const [zIndex, setZIndex] = useState(() => nextZIndex())
  const interactionRef = useRef<
    | {
        type: "move"
        startX: number
        startY: number
        rect: WindowRect
      }
    | {
        type: "resize"
        startX: number
        startY: number
        rect: WindowRect
        direction: ResizeDirection
      }
    | null
  >(null)

  const bringToFront = useCallback(() => {
    setZIndex(nextZIndex())
  }, [])

  const restore = useCallback(() => {
    setMinimized(false)
    bringToFront()
  }, [bringToFront])

  useEffect(() => {
    if (!open) {
      setMinimized(false)
      dock?.unregisterDockItem(windowId)
    }
  }, [dock, open, windowId])

  useEffect(() => {
    if (!open) {
      return
    }
    if (minimized) {
      dock?.registerDockItem({
        id: windowId,
        title,
        onRestore: restore,
        onClose,
      })
      return
    }
    dock?.unregisterDockItem(windowId)
  }, [dock, minimized, onClose, open, restore, title, windowId])

  useEffect(() => {
    if (!open || minimized) {
      return
    }
    const handleResize = () => {
      setRect((current) => clampRect(current, minWidth, minHeight))
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [minHeight, minWidth, minimized, open])

  useEffect(() => {
    if (!open) {
      return
    }
    bringToFront()
    setRect((current) => clampRect(current, minWidth, minHeight))
  }, [bringToFront, minHeight, minWidth, open])

  useEffect(() => {
    if (!open) {
      return
    }
    const handlePointerMove = (event: PointerEvent) => {
      const activeInteraction = interactionRef.current
      if (!activeInteraction) {
        return
      }
      if (activeInteraction.type === "move") {
        setRect(
          clampRect(
            {
              ...activeInteraction.rect,
              x: activeInteraction.rect.x + (event.clientX - activeInteraction.startX),
              y: activeInteraction.rect.y + (event.clientY - activeInteraction.startY),
            },
            minWidth,
            minHeight,
          ),
        )
        return
      }
      setRect(
        applyResize({
          rect: activeInteraction.rect,
          direction: activeInteraction.direction,
          deltaX: event.clientX - activeInteraction.startX,
          deltaY: event.clientY - activeInteraction.startY,
          minWidth,
          minHeight,
        }),
      )
    }
    const handlePointerUp = () => {
      interactionRef.current = null
    }
    window.addEventListener("pointermove", handlePointerMove)
    window.addEventListener("pointerup", handlePointerUp)
    return () => {
      window.removeEventListener("pointermove", handlePointerMove)
      window.removeEventListener("pointerup", handlePointerUp)
    }
  }, [minHeight, minWidth, open])

  const handleShellStyle: CSSProperties = useMemo(
    () =>
      fullscreen
        ? {
            left: 0,
            top: 0,
            width: "100vw",
            height: "100vh",
            zIndex,
          }
        : {
            left: rect.x,
            top: rect.y,
            width: rect.width,
            height: rect.height,
            zIndex,
          },
    [fullscreen, rect.height, rect.width, rect.x, rect.y, zIndex],
  )

  const resizeHandles: Array<{
    direction: ResizeDirection
    className: string
  }> = [
    { direction: "top", className: "left-4 right-4 top-[-4px] h-2 cursor-ns-resize" },
    { direction: "right", className: "bottom-4 right-[-4px] top-4 w-2 cursor-ew-resize" },
    { direction: "bottom", className: "bottom-[-4px] left-4 right-4 h-2 cursor-ns-resize" },
    { direction: "left", className: "bottom-4 left-[-4px] top-4 w-2 cursor-ew-resize" },
    { direction: "top-left", className: "left-[-4px] top-[-4px] h-4 w-4 cursor-nwse-resize" },
    { direction: "top-right", className: "right-[-4px] top-[-4px] h-4 w-4 cursor-nesw-resize" },
    { direction: "bottom-left", className: "bottom-[-4px] left-[-4px] h-4 w-4 cursor-nesw-resize" },
    { direction: "bottom-right", className: "bottom-[-4px] right-[-4px] h-4 w-4 cursor-nwse-resize" },
  ]

  if (!open || minimized) {
    return null
  }

  return (
    <div className="pointer-events-none fixed inset-0 z-[1100]">
      <section
        className={cn(
          "pointer-events-auto fixed flex min-h-0 flex-col overflow-hidden border border-slate-200 bg-white shadow-lg",
          fullscreen ? "rounded-none" : "rounded-[24px]",
          className,
        )}
        style={handleShellStyle}
        role="dialog"
        aria-modal="false"
        onPointerDown={bringToFront}
      >
        <header
          className={cn(
            "flex select-none items-start justify-between gap-4 border-b border-slate-200 px-5 py-4",
            fullscreen ? "cursor-default" : "cursor-move",
          )}
          onPointerDown={(event) => {
            if (fullscreen || event.button !== 0) {
              return
            }
            interactionRef.current = {
              type: "move",
              startX: event.clientX,
              startY: event.clientY,
              rect,
            }
          }}
        >
          <div className="min-w-0">
            <h3 className="m-0 truncate text-slate-900">{title}</h3>
            {description ? <p className="m-0 text-sm text-slate-500">{description}</p> : null}
          </div>
          <div
            className="flex flex-wrap items-center gap-2"
            onPointerDown={(event) => event.stopPropagation()}
          >
            {headerExtras}
            {allowFullscreen ? (
              <ShellButton variant="ghost" onClick={() => setFullscreen((current) => !current)}>
                {fullscreen ? "Окно" : "На весь экран"}
              </ShellButton>
            ) : null}
            {allowMinimize ? (
              <ShellButton variant="ghost" onClick={() => setMinimized(true)}>
                Свернуть
              </ShellButton>
            ) : null}
            <ShellButton variant="ghost" onClick={onClose}>
              Закрыть
            </ShellButton>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">{children}</div>

        {footer ? (
          <footer className="border-t border-slate-200 px-5 py-4">
            {footer}
          </footer>
        ) : null}

        {fullscreen
          ? null
          : resizeHandles.map((handle) => (
              <div
                key={handle.direction}
                className={cn("absolute", handle.className)}
                onPointerDown={(event) => {
                  if (event.button !== 0) {
                    return
                  }
                  event.stopPropagation()
                  bringToFront()
                  interactionRef.current = {
                    type: "resize",
                    direction: handle.direction,
                    startX: event.clientX,
                    startY: event.clientY,
                    rect,
                  }
                }}
              />
            ))}
      </section>
    </div>
  )
}
